import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { stripeService } from '../services/stripe';
import { authService } from '../services/auth';

export default function Authenticate() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('authenticating'); // authenticating, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState(''); // 'link_used', 'link_expired', 'generic'
  const hasRun = React.useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasRun.current) return;
    hasRun.current = true;

    const authenticate = async () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');
        const tokenType = urlParams.get('stytch_token_type');

        if (!token || !tokenType) {
          setStatus('error');
          setErrorType('generic');
          setErrorMessage('Invalid authentication link. Please request a new magic link.');
          return;
        }

        // Authenticate with backend
        const response = await authService.authenticate(token, tokenType);

        // Save session data
        if (response.session_token) {
          sessionStorage.setItem('session_token', response.session_token);
        }
        if (response.session_jwt) {
          sessionStorage.setItem('session_jwt', response.session_jwt);
        }
        if (response.member) {
          sessionStorage.setItem('user', JSON.stringify(response.member));
        }
        await stripeService.claimPendingCheckout(); // link a paid public-signup checkout to this account before leaving

        setStatus('success');
        
        // Auto-redirect to dashboard after a brief delay
        // Use window.location.replace to ensure same-tab behavior
        // and proper history replacement
        setTimeout(() => {
          // Use location.replace for maximum compatibility 
          // This replaces the current history entry, preventing back-button issues
          window.location.replace('/dashboard');
        }, 1500);

      } catch (error) {
        console.error('Authentication error:', error);
        setStatus('error');
        
        // Parse error details
        const errorDetail = error.response?.data?.detail || error.message || '';
        const errorTypeFromResponse = error.response?.data?.error_type || '';
        
        // Check for magic link already used/expired error
        if (
          errorDetail.includes('unable_to_auth_magic_link') ||
          errorTypeFromResponse === 'unable_to_auth_magic_link' ||
          errorDetail.includes('already used') ||
          errorDetail.includes('expired')
        ) {
          setErrorType('link_used');
          setErrorMessage('This login link has already been used or has expired.');
        } else if (errorDetail.includes('invalid') || errorDetail.includes('Invalid')) {
          setErrorType('link_expired');
          setErrorMessage('This login link is invalid or has expired.');
        } else {
          setErrorType('generic');
          setErrorMessage('Authentication failed. Please try again.');
        }
      }
    };

    authenticate();
  }, [location, navigate]);

  // Handle navigation back to login - uses same window/tab
  const handleBackToLogin = () => {
    // Clear any stale session data
    sessionStorage.removeItem('session_token');
    sessionStorage.removeItem('session_jwt');
    sessionStorage.removeItem('user');
    
    // Navigate in same window (replace history entry)
    navigate('/merchant/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
      <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-8 w-full max-w-md shadow-[0_0_0_1px_rgba(62,241,199,0.06)]">
        <div className="text-center">
          {status === 'authenticating' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#3EF1C7]/10 mb-4">
                <svg className="w-8 h-8 text-[#3EF1C7] animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Verifying your link...</h2>
              <p className="text-sm text-[#9CB0C7]">Please wait while we authenticate you</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#8CF78E]/10 mb-4">
                <svg className="w-8 h-8 text-[#8CF78E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Authentication successful!</h2>
              <p className="text-sm text-[#9CB0C7]">Redirecting to dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              {/* Icon based on error type */}
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                errorType === 'link_used' ? 'bg-[#F5A623]/10' : 'bg-[#F55454]/10'
              }`}>
                {errorType === 'link_used' ? (
                  // Info/warning icon for link already used
                  <svg className="w-8 h-8 text-[#F5A623]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  // Error icon for other errors
                  <svg className="w-8 h-8 text-[#F55454]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>

              {/* Title based on error type */}
              <h2 className="text-xl font-semibold text-white mb-2">
                {errorType === 'link_used' 
                  ? 'Login Link Already Used' 
                  : 'Authentication Failed'}
              </h2>

              {/* User-friendly message */}
              <p className="text-sm text-[#9CB0C7] mb-2">{errorMessage}</p>
              
              {/* Additional help text for link_used */}
              {errorType === 'link_used' && (
                <p className="text-xs text-[#6B7C93] mb-6">
                  Magic links can only be used once for security. Please request a new link to sign in.
                </p>
              )}

              {errorType !== 'link_used' && <div className="mb-6"></div>}

              <button
                onClick={handleBackToLogin}
                className="px-6 py-2 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 font-medium"
                data-testid="back-to-login-button"
              >
                Request New Login Link
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
